import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';
import MediaError from '@fec/frontend-aron/elements/media/MediaError/MediaError';
import MediaLoading from '@fec/frontend-aron/elements/media/MediaLoading/MediaLoading';
import Button from '@fec/frontend/elements/buttons/button/button';
import {
  MEDIA_CLICKED,
  MEDIA_PLAYING,
  onEvent,
  SET_META_THEME_COLOR,
  TEASER_VIDEO_CLICK,
  triggerEvent,
  VIDEO_MODAL_CLOSED,
} from '@fec/assets/js/utils/event';
import Urn from '@fec/frontend/foundation/client/urn';
import useOnClickOutside from '@fec/frontend-aron/foundation/hooks/useOnClickOutside';

const ModalVideo = lazy(
  () => import('@fec/frontend-aron/modules/media/ModalVideo/ModalVideo'),
);

import './PlayerModal.scss';

export function init() {
  const modalContainer = document.querySelector('.js-player-modal');

  if (modalContainer) {
    const root = createRoot(modalContainer);

    root.render(
      <React.StrictMode>
        <PlayerModal />
      </React.StrictMode>,
    );
  }
}

const getMetaThemeColor = (ref) => {
  const bgColor = window
    .getComputedStyle(ref.current)
    .getPropertyValue('--t-modal-bg');
  return bgColor;
};

const PlayerModal = () => {
  const modalRef = useRef(null);
  // we need to keep track of whether the event listeners have been initialized
  const listenersInitialized = useRef(false);
  // sometimes there are multiple instances of the same video in different teasers on a page -> we need to be able to keep them apart
  const [callerInfo, setCallerInfo] = useState(null);

  useEffect(() => {
    const handleVideoClick = ({ assetId, id }) => {
      setCallerInfo((prevCallerInfo) => {
        // we close the modal if the same teaser was clicked again or if it's no a video
        const isSameTeaser =
          id === prevCallerInfo?.elementId &&
          assetId === prevCallerInfo?.assetUrn;

        if (isSameTeaser || Urn.getType(assetId) !== 'video') {
          return null;
        }
        return {
          assetUrn: assetId,
          elementId: id,
        };
      });
    };

    if (listenersInitialized?.current) {
      return;
    }
    listenersInitialized.current = true;

    onEvent({
      eventName: TEASER_VIDEO_CLICK,
      eventHandler: ({ detail }) => handleVideoClick(detail),
    });
  }, [listenersInitialized?.current, callerInfo]);

  // close the modal when a different medium starts playing
  useEffect(() => {
    const handleMediaPlaying = (event) => {
      if (event.detail.frontendId !== callerInfo?.elementId) {
        closeModal();
      }
    };

    // media clicked is faster but only triggered once --> listen to both CLICKED and PLAYING
    window.addEventListener(MEDIA_CLICKED, handleMediaPlaying);
    window.addEventListener(MEDIA_PLAYING, handleMediaPlaying);
    return () => {
      window.removeEventListener(MEDIA_CLICKED, handleMediaPlaying);
      window.removeEventListener(MEDIA_PLAYING, handleMediaPlaying);
    };
  }, [callerInfo]);

  // when assetUrn changes, open or close the modal
  useEffect(() => {
    if (!modalRef?.current) {
      return;
    }

    if (callerInfo) {
      modalRef.current.show();
      triggerEvent(SET_META_THEME_COLOR, {
        color: getMetaThemeColor(modalRef),
      });
    } else {
      triggerEvent(SET_META_THEME_COLOR);
      triggerEvent(VIDEO_MODAL_CLOSED);
      modalRef.current.close();
    }
  }, [callerInfo, modalRef?.current]);

  const closeModal = () => setCallerInfo(null);

  // Close the modal if clicked outside
  useOnClickOutside(modalRef, closeModal, callerInfo !== null);

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      closeModal();
    }
  };

  return (
    <dialog ref={modalRef} onKeyDown={handleKeyDown} className="player-modal">
      <div className="player-modal__player-container">
        {callerInfo && (
          <Suspense fallback={<MediaLoading type="video" />}>
            <ErrorBoundary fallback={<MediaError />}>
              <ModalVideo
                urn={callerInfo.assetUrn}
                frontendId={callerInfo.elementId}
              />
            </ErrorBoundary>
          </Suspense>
        )}
      </div>

      <div className="player-modal__close">
        <Button
          styleModifier="button--transparent button--align-left button--no-spacing button--icon-button button--mixed-case button--no-text-on-mobile"
          svgIcon="svg-icon--close"
          buttonText="Schliessen"
          onClick={closeModal}
        />
      </div>

      {/* the last element in the dialog - when it is focussed, we close the dialog immediately. The text is not read aloud. */}
      <button
        className="h-offscreen h-offscreen-focusable"
        onFocus={closeModal}>
        Ende des Video-Modals erreicht, es wird nun geschlossen.
      </button>
    </dialog>
  );
};
